import { useState } from "react";
import { UploadIcon } from "lucide-react";
import { Button } from "@component/button";
import { cn } from "@util/tailwind";

interface FileUploadProps {
    label: string;
    accept?: string;
    onChange: (file: File | null) => void;
    className?: string;
    value?: File | null;
}

export default function FileUpload({
    label,
    accept = "image/*",
    onChange,
    className,
    value,
}: FileUploadProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [preview, setPreview] = useState<string | null>(null);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            handleFile(file);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            handleFile(file);
        }
    };

    const handleFile = (file: File) => {
        onChange(file);

        // Create preview for images
        if (file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = () => {
                setPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        } else {
            setPreview(null);
        }
    };

    const handleRemove = () => {
        onChange(null);
        setPreview(null);
    };

    return (
        <div
            className={cn("w-full", className)}
            data-pol-id="s73oqb"
            data-pol-file-name="file-upload"
            data-pol-file-type="component"
        >
            {value || preview ? (
                <div
                    className="flex flex-col items-center gap-4"
                    data-pol-id="eum2ji"
                    data-pol-file-name="file-upload"
                    data-pol-file-type="component"
                >
                    {preview && (
                        <div
                            className="relative h-24 w-24 overflow-hidden rounded-md"
                            data-pol-id="d6px3x"
                            data-pol-file-name="file-upload"
                            data-pol-file-type="component"
                        >
                            <img
                                src={preview}
                                alt="Preview"
                                className="h-full w-full object-cover"
                                data-pol-id="5uzpfp"
                                data-pol-file-name="file-upload"
                                data-pol-file-type="component"
                            />
                        </div>
                    )}
                    <div
                        className="flex gap-2"
                        data-pol-id="alfce2"
                        data-pol-file-name="file-upload"
                        data-pol-file-type="component"
                    >
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => document.getElementById(label)?.click()}
                            data-pol-id="56v9t0"
                            data-pol-file-name="file-upload"
                            data-pol-file-type="component"
                        >
                            Change {label}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleRemove}
                            data-pol-id="k0fk93"
                            data-pol-file-name="file-upload"
                            data-pol-file-type="component"
                        >
                            Remove
                        </Button>
                    </div>
                    <input
                        id={label}
                        type="file"
                        accept={accept}
                        onChange={handleFileChange}
                        className="hidden"
                        data-pol-id="30bn37"
                        data-pol-file-name="file-upload"
                        data-pol-file-type="component"
                    />
                </div>
            ) : (
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={cn(
                        "flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors",
                        isDragging
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-300 hover:border-blue-500 hover:bg-gray-50",
                    )}
                    data-pol-id="qaumnw"
                    data-pol-file-name="file-upload"
                    data-pol-file-type="component"
                >
                    <div
                        className="mb-4 rounded-full bg-blue-100 p-3"
                        data-pol-id="gcpmwo"
                        data-pol-file-name="file-upload"
                        data-pol-file-type="component"
                    >
                        <UploadIcon
                            className="h-6 w-6 text-blue-600"
                            data-pol-id="bawh3r"
                            data-pol-file-name="file-upload"
                            data-pol-file-type="component"
                        />
                    </div>
                    <p
                        className="mb-2 text-sm font-medium text-gray-700"
                        data-pol-id="qzennl"
                        data-pol-file-name="file-upload"
                        data-pol-file-type="component"
                    >
                        Upload {label}
                    </p>
                    <p
                        className="mb-4 text-xs text-gray-500"
                        data-pol-id="sg9nfi"
                        data-pol-file-name="file-upload"
                        data-pol-file-type="component"
                    >
                        Drag and drop or click to browse
                    </p>
                    <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => document.getElementById(label)?.click()}
                        data-pol-id="2uvc8t"
                        data-pol-file-name="file-upload"
                        data-pol-file-type="component"
                    >
                        Browse files
                    </Button>
                    <input
                        id={label}
                        type="file"
                        accept={accept}
                        onChange={handleFileChange}
                        className="hidden"
                        data-pol-id="t39x1m"
                        data-pol-file-name="file-upload"
                        data-pol-file-type="component"
                    />
                </div>
            )}
        </div>
    );
}
